import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { WorkOrderServiceType, WorkOrderServiceTypeLabels, WorkOrderSourceLabels, WorkOrderStatus, WorkOrderStatusLabels, WorkOrderPriority, WorkOrderPriorityLabels, WORK_ORDER_OVERDUE_STATUS_LABEL } from '../../../enums/work-order.enum';
import { WorkshopOption } from '../../../models/work-order.models';
import { AuthService } from '../../../../../../_shared/services';
import { CurrencyHelper } from '../../../../../../_shared/helpers/currency/currency-map.helper';
import { WorkshopFilterService } from '../../../../../_shared/services/workshop-filter.service';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { FlatpickrDatePickerComponent } from '../../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { WorkOrdersOverviewService } from '../../../services/work-orders-overview.service';
import { UpdateWorkOrderEditRequest, ViewDetailsFormData } from '../../../models/work-orders-overview.models';
import { WorkOrderTechnician } from '../../../models/technician.model';
import { PartInventoryItem } from '../../../../../parts-and-inventory/parts/models/inventory-overview.model';
import { PartsAndInventoryService } from '../../../../../parts-and-inventory/parts/services/parts-and-inventory.service';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-view-details-modal',
  standalone: true,
  imports: [NgbModule, CommonModule, FormsModule, FlatpickrDatePickerComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './view-details-modal.component.html',
  styleUrl: './view-details-modal.component.scss'
})
export class ViewDetailsModalComponent implements OnInit, OnDestroy {
  readonly OVERDUE_STATUS = 'overdue';

  @Input() workOrderId: number | null = null;
  workshops: WorkshopOption[] = [];

  private authService = inject(AuthService);
  private workshopFilterService = inject(WorkshopFilterService);
  private workOrdersService = inject(WorkOrdersOverviewService);
  private partsService = inject(PartsAndInventoryService);
  private errorHandlerService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);
  private modalInstance: NgbActiveModal = inject(NgbActiveModal);
  private transloco = inject(TranslocoService);

  private cancelSubject = new Subject<void>();

  currencySymbol: string = 'R';
  techniciansList: WorkOrderTechnician[] = [];

  formData: ViewDetailsFormData = {
    workOrderId: '',
    id: 0,
    assetId: null,
    assetName: '',
    source: null,
    serviceType: null,
    technicianId: null,
    technicianName: '',
    technicianSpecialization: '',
    scheduledDate: null,
    workshopId: null,
    workshopName: '',
    workType: '',
    reason: '',
    estimatedCost: null,
    estimatedTime: null,
    enableEmailAlerts: false,
    partsRequired: [],
    downtime: '',
    status: WorkOrderStatus.All,
    priority: WorkOrderPriority.All,
    comment: ''
  };

  scheduledDateString: string = '';
  isLoading: boolean = false;
  isWorkOrderLoading: boolean = false;
  isWorkshopsLoading: boolean = false;
  isPartsLoading: boolean = false;
  scheduleDateTouched: boolean = false;
  timeSuffixLeft: number = 24;
  newPartInput: string = '';

  // Parts search
  private readonly partsPageSize: number = 20;
  private readonly partsSearchDebounceMs: number = 300;

  partsSearchResults: PartInventoryItem[] = [];
  partsDropdownOpen: boolean = false;
  isSearchingParts: boolean = false;

  private partsRequestSequence: number = 0;
  private partsSearchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  // Dropdown states
  serviceTypeDropdownOpen: boolean = false;
  workshopDropdownOpen: boolean = false;
  priorityDropdownOpen: boolean = false;

  serviceTypes = [
    { id: WorkOrderServiceType.MinorService, name: WorkOrderServiceTypeLabels[WorkOrderServiceType.MinorService] },
    { id: WorkOrderServiceType.IntermediateService, name: WorkOrderServiceTypeLabels[WorkOrderServiceType.IntermediateService] },
    { id: WorkOrderServiceType.MajorService, name: WorkOrderServiceTypeLabels[WorkOrderServiceType.MajorService] }
  ];

  priorities = [
    { id: WorkOrderPriority.Low, name: WorkOrderPriorityLabels[WorkOrderPriority.Low] },
    { id: WorkOrderPriority.Medium, name: WorkOrderPriorityLabels[WorkOrderPriority.Medium] },
    { id: WorkOrderPriority.High, name: WorkOrderPriorityLabels[WorkOrderPriority.High] }
  ];

  // Expose enum to template
  WorkOrderStatus = WorkOrderStatus;

  ngOnInit(): void {
    const currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(currencyCode);
    this.isWorkOrderLoading = true;

    void this.initializeFormAsync();
  }

  ngOnDestroy(): void {
    if (this.partsSearchDebounceTimer) {
      clearTimeout(this.partsSearchDebounceTimer);
      this.partsSearchDebounceTimer = null;
    }

    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  get selectedWorkOrderId(): string {
    return this.formData.workOrderId || '';
  }


  get selectedAssetName(): string {
    return this.formData.assetName || '';
  }

  get selectedServiceTypeName(): string {
    if (this.formData.serviceType === null) return '';
    const key = WorkOrderServiceTypeLabels[this.formData.serviceType];
    return key ? this.transloco.translate(key) : '';
  }

  get selectedWorkshopName(): string {
    return this.workshops.find(w => w.id === this.formData.workshopId)?.name || this.formData.workshopName || '';
  }

  get selectedSourceName(): string {
    if (this.formData.source === null) return '';
    const key = WorkOrderSourceLabels[this.formData.source];
    return key ? this.transloco.translate(key) : '';
  }

  get selectedPriorityName(): string {
    if (this.isWorkOrderLoading) return this.transloco.translate('workOrder.modals.viewDetails.loadingPriority');
    const key = WorkOrderPriorityLabels[this.formData.priority];
    return key ? this.transloco.translate(key) : '';
  }

  get isInputLoading(): boolean {
    return this.isWorkOrderLoading || this.isWorkshopsLoading;
  }

  get workOrderIdDisplayText(): string {
    if (this.isWorkOrderLoading) return this.transloco.translate('workOrder.modals.shared.loadingWorkOrderId');
    return this.selectedWorkOrderId;
  }

  get assetDisplayText(): string {
    if (this.isWorkOrderLoading) return this.transloco.translate('workOrder.modals.shared.loadingAsset');
    return this.selectedAssetName;
  }

  get sourceDisplayText(): string {
    if (this.isWorkOrderLoading) return this.transloco.translate('workOrder.modals.shared.loadingSource');
    return this.selectedSourceName;
  }

  get serviceTypeDisplayText(): string {
    if (this.isWorkOrderLoading) return this.transloco.translate('workOrder.modals.shared.loadingServiceType');
    return this.selectedServiceTypeName || this.transloco.translate('workOrder.modals.shared.selectServiceType');
  }

  get workshopDisplayText(): string {
    if (this.isWorkOrderLoading || this.isWorkshopsLoading) return this.transloco.translate('workOrder.modals.shared.loadingWorkshop');
    return this.selectedWorkshopName || this.transloco.translate('workOrder.modals.shared.selectWorkshop');
  }

  get technicianDisplayText(): string {
    if (this.isWorkOrderLoading) return this.transloco.translate('workOrder.modals.viewDetails.loadingTechnician');
    return this.formData.technicianName;
  }

  get maintenanceReasonDisplayText(): string {
    if (this.isWorkOrderLoading) return this.transloco.translate('workOrder.modals.shared.loadingMaintenanceReason');
    return this.formData.localizedReason || this.formData.reason;
  }

  get statusLabel(): string {
    if (this.isWorkOrderLoading) return this.transloco.translate('workOrder.modals.viewDetails.loadingStatus');
    if (this.displayStatus === this.OVERDUE_STATUS) {
      return this.transloco.translate(WORK_ORDER_OVERDUE_STATUS_LABEL);
    }
    const key = WorkOrderStatusLabels[this.displayStatus];
    return key ? this.transloco.translate(key) : '';
  }

  get downtimeDisplayText(): string {
    if (this.isWorkOrderLoading) return this.transloco.translate('workOrder.modals.viewDetails.loadingDowntime');
    return this.formData.downtime || '';
  }

  get displayStatus(): WorkOrderStatus | typeof this.OVERDUE_STATUS {
    if (
      this.formData.status === WorkOrderStatus.Open &&
      this.hasScheduledDatePassed(this.formData.scheduledDate)
    ) {
      return this.OVERDUE_STATUS;
    }

    return this.formData.status;
  }

  get statusClass(): string {
    switch (this.displayStatus) {
      case WorkOrderStatus.Pending:
        return 'status-overdue';
      case WorkOrderStatus.Open:
        return 'status-open';
      case WorkOrderStatus.InProgress:
        return 'status-in-progress';
      case WorkOrderStatus.Completed:
        return 'status-complete';
      case WorkOrderStatus.Rejected:
        return 'status-overdue';
      case this.OVERDUE_STATUS:
        return 'status-overdue';
      default:
        return '';
    }
  }

  private hasScheduledDatePassed(scheduledDate: Date | null): boolean {
    if (!scheduledDate) {
      return false;
    }

    const date = new Date(scheduledDate);
    if (Number.isNaN(date.getTime())) {
      return false;
    }

    return date <= new Date();
  }

  get isFormValid(): boolean {
    return (
      this.formData.workOrderId !== '' &&
      this.formData.assetId !== null &&
      this.formData.source !== null &&
      this.formData.serviceType !== null &&
      this.scheduledDateString !== '' &&
      this.hasValidWorkshopSelection &&
      !this.isInputLoading
    );
  }

  get hasValidWorkshopSelection(): boolean {
    if (this.formData.workshopId === null) {
      return false;
    }

    return this.workshops.some(workshop => workshop.id === this.formData.workshopId);
  }

  get isFieldsDisabled(): boolean {
    return this.formData.status === WorkOrderStatus.InProgress ||
      this.formData.status === WorkOrderStatus.Completed;
  }

  // Dropdown toggle methods
  toggleServiceTypeDropdown(): void {
    if (this.isInputLoading) return;
    this.serviceTypeDropdownOpen = !this.serviceTypeDropdownOpen;
    this.closeOtherDropdowns('serviceType');
  }

  toggleWorkshopDropdown(): void {
    if (this.isInputLoading) return;
    this.workshopDropdownOpen = !this.workshopDropdownOpen;
    this.closeOtherDropdowns('workshop');
  }

  togglePriorityDropdown(): void {
    this.priorityDropdownOpen = !this.priorityDropdownOpen;
    this.closeOtherDropdowns('priority');
  }

  private closeOtherDropdowns(except: string): void {
    if (except !== 'serviceType') this.serviceTypeDropdownOpen = false;
    if (except !== 'workshop') this.workshopDropdownOpen = false;
    if (except !== 'priority') this.priorityDropdownOpen = false;
  }

  closeAllDropdowns(): void {
    this.serviceTypeDropdownOpen = false;
    this.workshopDropdownOpen = false;
    this.priorityDropdownOpen = false;
    this.partsDropdownOpen = false;
  }

  // Selection handlers
  selectServiceType(serviceType: { id: WorkOrderServiceType; name: string }): void {
    this.formData.serviceType = serviceType.id;
    this.serviceTypeDropdownOpen = false;

    // Auto-set priority based on service type
    switch (serviceType.id) {
      case WorkOrderServiceType.MajorService:
        this.formData.priority = WorkOrderPriority.High;
        break;
      case WorkOrderServiceType.IntermediateService:
        this.formData.priority = WorkOrderPriority.Medium;
        break;
      case WorkOrderServiceType.MinorService:
        this.formData.priority = WorkOrderPriority.Low;
        break;
    }
  }

  selectWorkshop(workshop: WorkshopOption): void {
    this.formData.workshopId = workshop.id;
    this.formData.workshopName = workshop.name;
    this.workshopDropdownOpen = false;
  }

  selectPriority(priority: { id: WorkOrderPriority; name: string }): void {
    this.formData.priority = priority.id;
    this.priorityDropdownOpen = false;
  }

  // Parts management
  addPart(): void {
    if (this.newPartInput.trim()) {
      this.formData.partsRequired.push(this.newPartInput.trim());
      this.newPartInput = '';
      this.partsDropdownOpen = false;
      this.partsSearchResults = [];
    }
  }

  removePart(index: number): void {
    this.formData.partsRequired.splice(index, 1);
  }

  onPartInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addPart();
    } else if (event.key === 'Escape') {
      this.partsDropdownOpen = false;
    }
  }

  onPartInputChange(): void {
    if (this.partsSearchDebounceTimer) {
      clearTimeout(this.partsSearchDebounceTimer);
      this.partsSearchDebounceTimer = null;
    }

    const query = this.newPartInput.trim();

    if (query.length < 1) {
      // Invalidate any in-flight search so a late response can't reopen the dropdown
      this.partsRequestSequence++;
      this.isSearchingParts = false;
      this.partsDropdownOpen = false;
      this.partsSearchResults = [];
      return;
    }

    this.isSearchingParts = true;
    this.partsDropdownOpen = true;

    this.partsSearchDebounceTimer = setTimeout(() => {
      this.partsSearchDebounceTimer = null;
      void this.searchPartsAsync(query);
    }, this.partsSearchDebounceMs);
  }

  selectPart(part: PartInventoryItem): void {
    if (!this.formData.partsRequired.includes(part.partName)) {
      this.formData.partsRequired.push(part.partName);
    }
    this.newPartInput = '';
    this.partsDropdownOpen = false;
    this.partsSearchResults = [];
  }

  private async searchPartsAsync(query: string): Promise<void> {
    const requestId = ++this.partsRequestSequence;

    try {
      const response = await this.partsService.getPaginatedPartsInventoryAsync({
        page: 1,
        pageSize: this.partsPageSize,
        searchTerm: query,
        accountIds: [],
        warehouseIds: [],
        categoryIds: []
      });

      if (requestId !== this.partsRequestSequence) {
        return;
      }

      // Filter out parts that are already selected
      const allResults = response.data?.results ?? [];
      this.partsSearchResults = allResults.filter(
        part => !this.formData.partsRequired.includes(part.partName)
      );
      if (requestId === this.partsRequestSequence) {
        this.isSearchingParts = false;
      }
    } catch (error: any) {
      if (requestId !== this.partsRequestSequence) {
        return;
      }
      this.partsSearchResults = [];
      if (requestId === this.partsRequestSequence) {
        this.isSearchingParts = false;
      }
    }
  }

  closePartsDropdown(): void {
    this.partsDropdownOpen = false;
  }

  // Date and time handlers
  onScheduledDateChange(value: string | undefined): void {
    this.scheduledDateString = value ?? '';
    this.scheduleDateTouched = true;
  }

  onTimeInputChange(): void {
    const value = this.formData.estimatedTime;
    if (value === null || value === undefined) {
      this.timeSuffixLeft = 18;
    } else {
      const valueStr = value.toString();
      const charWidth = 7;
      this.timeSuffixLeft = 14 + (valueStr.length * charWidth) + 6;
    }
  }

  // Save changes
  async saveChanges(): Promise<void> {
    if (!this.isFormValid) return;

    this.isLoading = true;

    try {
      let scheduledDate: Date | null = null;
      if (this.scheduledDateString) {
        const dateParts = this.scheduledDateString.split('/');
        const year = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1;
        const day = parseInt(dateParts[2], 10);
        const originalTime = this.formData.scheduledDate;
        scheduledDate = new Date(year, month, day, originalTime?.getHours() ?? 0, originalTime?.getMinutes() ?? 0);
      }

      if (!scheduledDate || Number.isNaN(scheduledDate.getTime())) {
        this.scheduleDateTouched = true;
        return;
      }

      const request: UpdateWorkOrderEditRequest = {
        workOrderId: this.formData.id,
        workType: this.formData.workType?.trim() ?? '',
        serviceType: this.formData.serviceType!,
        scheduledDate,
        workshopId: this.formData.workshopId!,
        enableEmailAlert: this.formData.enableEmailAlerts,
        priority: this.formData.priority,
        comment: this.formData.comment || undefined
      };

      await this.workOrdersService.updateWorkOrderEditAsync(request, this.cancelSubject);

      this.toastr.success(this.transloco.translate('workOrder.toasts.detailsUpdated'));
      this.isLoading = false;
      this.modalInstance.close(request);
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => this.isLoading = false
      });
    }
  }

  closeModal(): void {
    this.modalInstance.dismiss();
  }

  private formatDateForPicker(date: Date): string {
    const d = new Date(date);
    const day = d.getDate();
    const month = d.getMonth() + 1;
    const year = d.getFullYear();
    // Return format matching Flatpickr's Y/m/d format (YYYY/M/DD)
    return `${year}/${month}/${day}`;
  }

  private async initializeFormAsync(): Promise<void> {
    if (this.workOrderId === null) {
      this.closeModal();
      return;
    }

    try {
      await Promise.all([
        this.loadWorkshops(),
        this.loadWorkOrderById(this.workOrderId),
        this.loadIssuedPartsAsync(this.workOrderId)
      ]);
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => this.closeModal()
      });
    }
  }

  private async loadIssuedPartsAsync(workOrderId: number): Promise<void> {
    this.isPartsLoading = true;

    try {
      const response = await this.partsService.getIssuedPartsByWorkOrderAsync(workOrderId, this.cancelSubject);
      const partNames = (response.data ?? []).map(part => part.partName);
      this.formData.partsRequired = [...new Set(partNames)];
      this.isPartsLoading = false;
    } catch (error: any) {
      // A work order with no issued parts comes back as an error, not an empty list
      if (error?.error?.errorCode !== 'parts-inventory/work-order-parts-not-found') {
        this.errorHandlerService.handleError(error);
      }

      this.isPartsLoading = false;
    }
  }

  private async loadWorkOrderById(workOrderId: number): Promise<void> {
    this.isWorkOrderLoading = true;
    try {
      const response = await this.workOrdersService.getWorkOrderByIdAsync(workOrderId, this.cancelSubject);
      const workOrder = response.data;
      if (!workOrder) {
        return;
      }

      const scheduledDate = workOrder.scheduledDate ? new Date(workOrder.scheduledDate) : null;
      const downtimeLabel = workOrder.downtime === null || workOrder.downtime === undefined
        ? ''
        : typeof workOrder.downtime === 'number'
          ? this.transloco.translate('workOrder.modals.viewDetails.downtimeHours', { hours: workOrder.downtime })
          : `${workOrder.downtime}`;

      this.formData = {
        workOrderId: workOrder.workOrderNumber,
        id: workOrder.workOrderId,
        assetId: workOrder.assetId,
        assetName: workOrder.assetName,
        source: workOrder.source,
        serviceType: workOrder.serviceType,
        technicianId: workOrder.technicianId ?? null,
        technicianName: workOrder.technicianName ?? '',
        technicianSpecialization: workOrder?.jobTitle ?? '',
        scheduledDate,
        workshopId: workOrder.workshopId,
        workshopName: workOrder.workshopName,
        workType: workOrder.workType ?? '',
        reason: workOrder.reason ?? '',
        localizedReason: workOrder.localizedReason ?? null,
        estimatedCost: workOrder.estimatedCost ?? null,
        estimatedTime: workOrder.estimatedTime ?? null,
        enableEmailAlerts: workOrder.emailAlertEnabled ?? false,
        // Carried over: the issued parts load in parallel and may already have landed
        partsRequired: this.formData.partsRequired,
        downtime: downtimeLabel,
        status: workOrder.status,
        priority: workOrder.priority,
        comment: workOrder.comment ?? ''
      };

      this.scheduledDateString = scheduledDate ? this.formatDateForPicker(scheduledDate) : '';

      this.onTimeInputChange();
      this.isWorkOrderLoading = false
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => this.isWorkOrderLoading = false
      });
    }
  }

  private async loadWorkshops(): Promise<void> {
    this.isWorkshopsLoading = true;
    try {
      const response = await this.workshopFilterService.getWorkshopList([], this.cancelSubject);
      this.workshops = (response.data ?? []).map(workshop => ({
        id: workshop.id,
        name: workshop.name
      }));
      this.isWorkshopsLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => this.isWorkshopsLoading = false
      });
    }
  }
}